import { observer } from 'mobx-react';
import React from 'react';

import { UnresolvedIdpValue } from 'utils/api/modules/user/user.types';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';

import { IDP_FIELDS, IdpField } from './fields';
import { FixLink, Mismatch } from './styles';

/** Fields whose options an admin manages on the User Fields page. Sites are a module of their own. */
const OPTION_LIST_FIELDS: IdpField[] = [IDP_FIELDS.department, IDP_FIELDS.region, IDP_FIELDS.employmentType];

interface Advice {
  problem: string;
  action: string;
  to?: string;
}

export const unresolvedTestId = (field: IdpField): string => `idp-unresolved-${field}`;

export const UnresolvedValue: React.FunctionComponent<{ field: IdpField; unresolved?: UnresolvedIdpValue }> = ({
  field,
  unresolved,
}) => {
  const { session } = useStore();

  if (!unresolved) {
    return null;
  }

  const isSite = field === IDP_FIELDS.site;
  const subject = isSite ? 'site' : 'option';

  // Each link follows whatever the sidebar itself requires, so nobody is sent to a page they cannot open.
  const canReachSites = (session.user?.organisation?.maximumSitesAllowed ?? 0) > 0;
  const canReachUserFields = !!session.user?.showObjectManager;
  const destination = isSite
    ? canReachSites && routes.dashboard.sites.toString()
    : OPTION_LIST_FIELDS.includes(field) && canReachUserFields && routes.dashboard.settings.userFields.toString();

  const advice: Advice = {
    no_option: {
      problem: `doesn’t match any ${subject} here.`,
      action: isSite ? 'Add it in Sites' : 'Add it to the list',
      to: destination || undefined,
    },
    ambiguous: {
      problem: `matches more than one ${subject} here.`,
      action: isSite ? 'Rename one of them in Sites' : 'Rename one of them',
      to: destination || undefined,
    },
    invalid_value: { problem: 'can’t be stored in this field.', action: '' },
  }[unresolved.reason] ?? { problem: 'couldn’t be applied.', action: '' };

  return (
    <Mismatch data-testid={unresolvedTestId(field)}>
      “{unresolved.value}” from your identity provider {advice.problem}{' '}
      {advice.action ? (
        <>
          {advice.to ? <FixLink to={advice.to}>{advice.action}</FixLink> : advice.action}, or change what the field is
          mapped to.
        </>
      ) : (
        'Correct it in your identity provider.'
      )}
    </Mismatch>
  );
};

export default observer(UnresolvedValue);
